import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
const OptimalStockManagement = () => {
    const navigate = useNavigate();
    // 상태 관리
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchColumn, setSearchColumn] = useState('productCode');
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadedItems, setUploadedItems] = useState([]);
    const [fileInputKey, setFileInputKey] = useState(Date.now());
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // 샘플 데이터 로드
    useEffect(() => {
        // 실제 API 호출로 대체 가능
        const sampleData = [
            {
                id: 1,
                productCode: 'ITEM001',
                productName: '면 티셔츠',
                specification: '흰색, M 사이즈',
                image: 'https://via.placeholder.com/50',
                currentStock: 100,
                optimalStock: 150,
                actualStock: 95,
                reason: '시즌 상품으로 재고 유지 필요'
            },
            {
                id: 2,
                productCode: 'ITEM002',
                productName: '청바지',
                specification: '진청, 32인치',
                image: 'https://via.placeholder.com/50',
                currentStock: 75,
                optimalStock: 100,
                actualStock: 73,
                reason: '인기 상품으로 재고 확보 필요'
            },
            {
                id: 3,
                productCode: 'ITEM003',
                productName: '가죽 자켓',
                specification: '블랙, L 사이즈',
                image: 'https://via.placeholder.com/50',
                currentStock: 30,
                optimalStock: 50,
                actualStock: 28,
                reason: '계절성 아이템'
            },
            {
                id: 4,
                productCode: 'ITEM004',
                productName: '운동화',
                specification: '화이트, 265mm',
                image: 'https://via.placeholder.com/50',
                currentStock: 45,
                optimalStock: 60,
                actualStock: 42,
                reason: '상시 인기 아이템'
            },
            {
                id: 5,
                productCode: 'ITEM005',
                productName: '모자',
                specification: '베이지, 프리사이즈',
                image: 'https://via.placeholder.com/50',
                currentStock: 120,
                optimalStock: 80,
                actualStock: 118,
                reason: '과잉 재고 상태'
            }
        ];
        setItems(sampleData);
        setFilteredItems(sampleData);
    }, []);
    // 검색어 변경 이벤트 핸들러
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };
    // 검색 컬럼 변경 이벤트 핸들러
    const handleSearchColumnChange = (e) => {
        setSearchColumn(e.target.value);
    };
    // 검색 기능
    const handleSearch = () => {
        if (!searchTerm) {
            setFilteredItems(items);
            return;
        }
        const filtered = items.filter(item => {
            const value = item[searchColumn];
            if (typeof value === 'string') {
                return value.toLowerCase().includes(searchTerm.toLowerCase());
            }
            else if (typeof value === 'number') {
                return value.toString().includes(searchTerm);
            }
            return false;
        });
        setFilteredItems(filtered);
        setCurrentPage(1);
    };
    // 필터 초기화
    const clearFilters = () => {
        setSearchTerm('');
        setSearchColumn('productCode');
        setFilteredItems(items);
        setCurrentPage(1);
    };
    // 항목 선택 처리
    const handleSelectItem = (id) => {
        const newItems = items.map(item => {
            if (item.id === id) {
                return { ...item, selected: !item.selected };
            }
            return item;
        });
        setItems(newItems);
        const newFilteredItems = filteredItems.map(item => {
            if (item.id === id) {
                return { ...item, selected: !item.selected };
            }
            return item;
        });
        setFilteredItems(newFilteredItems);
        setSelectedItemIds(prevState => {
            if (prevState.includes(id)) {
                return prevState.filter(itemId => itemId !== id);
            }
            else {
                return [...prevState, id];
            }
        });
    };
    // 전체 선택/해제
    const handleSelectAll = () => {
        const allSelected = filteredItems.every(item => item.selected);
        const newFilteredItems = filteredItems.map(item => ({
            ...item,
            selected: !allSelected
        }));
        const newItems = items.map(item => {
            const found = newFilteredItems.find(i => i.id === item.id);
            if (found) {
                return { ...item, selected: found.selected };
            }
            return item;
        });
        setItems(newItems);
        setFilteredItems(newFilteredItems);
        if (allSelected) {
            setSelectedItemIds([]);
        }
        else {
            setSelectedItemIds(newFilteredItems.map(item => item.id));
        }
    };
    // 선택된 항목 삭제
    const handleDeleteSelected = () => {
        if (selectedItemIds.length === 0) {
            alert('삭제할 항목을 선택해주세요.');
            return;
        }
        if (window.confirm(`선택한 ${selectedItemIds.length}개 항목을 삭제하시겠습니까?`)) {
            const newItems = items.filter(item => !selectedItemIds.includes(item.id));
            const newFilteredItems = filteredItems.filter(item => !selectedItemIds.includes(item.id));
            setItems(newItems);
            setFilteredItems(newFilteredItems);
            setSelectedItemIds([]);
        }
    };
    // 단일 항목 삭제
    const handleDeleteItem = (id) => {
        if (window.confirm('이 항목을 삭제하시겠습니까?')) {
            const newItems = items.filter(item => item.id !== id);
            const newFilteredItems = filteredItems.filter(item => item.id !== id);
            setItems(newItems);
            setFilteredItems(newFilteredItems);
            setSelectedItemIds(prev => prev.filter(itemId => itemId !== id));
        }
    };
    // 항목 편집으로 이동
    const handleEditItem = (id) => {
        navigate(`/optimal-stock/edit/${id}`);
    };
    // 모달 열기/닫기
    const toggleUploadModal = () => {
        setIsUploadModalOpen(!isUploadModalOpen);
        if (!isUploadModalOpen) {
            setUploadedItems([]);
            setFileInputKey(Date.now());
        }
    };
    // 엑셀 파일 처리
    const handleFileUpload = (e) => {
        var _a;
        const file = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            var _a;
            try {
                const data = (_a = evt.target) === null || _a === void 0 ? void 0 : _a.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);
                // 엑셀 데이터를 적정재고 아이템 형식으로 변환
                const uploadedData = json.map((row, index) => ({
                    id: Date.now() + index,
                    productCode: row['상품코드'] || '',
                    productName: row['상품명'] || '',
                    specification: row['규격'] || '',
                    image: '',
                    currentStock: parseInt(row['장부수량'] || '0', 10),
                    optimalStock: parseInt(row['적정재고수량'] || '0', 10),
                    actualStock: parseInt(row['실재고수량'] || '0', 10),
                    reason: row['사유'] || '',
                    selected: false
                }));
                setUploadedItems(uploadedData);
            }
            catch (error) {
                console.error('Excel file parsing error:', error);
                alert('엑셀 파일 처리 중 오류가 발생했습니다.');
            }
        };
        reader.onerror = () => {
            alert('파일을 읽는 중 오류가 발생했습니다.');
        };
        reader.readAsBinaryString(file);
    };
    // 업로드된 아이템 등록
    const handleRegisterUploadedItems = () => {
        if (uploadedItems.length === 0) {
            alert('등록할 데이터가 없습니다.');
            return;
        }
        // 업로드된 아이템을 기존 아이템에 추가
        const newItems = [...items, ...uploadedItems];
        setItems(newItems);
        setFilteredItems(newItems);
        // 모달 닫기 및 상태 초기화
        setIsUploadModalOpen(false);
        setUploadedItems([]);
        setFileInputKey(Date.now());
        alert(`${uploadedItems.length}개 항목이 등록되었습니다.`);
    };
    // 템플릿 다운로드
    const handleDownloadTemplate = () => {
        const template = [
            {
                '상품코드': '',
                '상품명': '',
                '규격': '',
                '장부수량': '',
                '적정재고수량': '',
                '실재고수량': '',
                '사유': ''
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(template);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '적정재고등록');
        XLSX.writeFile(workbook, '적정재고등록_템플릿.xlsx');
    };
    // 선택한 항목 엑셀 다운로드
    const handleDownloadSelected = () => {
        if (selectedItemIds.length === 0) {
            alert('다운로드할 항목을 선택해주세요.');
            return;
        }
        const selectedItems = items.filter(item => selectedItemIds.includes(item.id));
        const excelData = selectedItems.map(item => ({
            '상품코드': item.productCode,
            '상품명': item.productName,
            '규격': item.specification,
            '장부수량': item.currentStock,
            '적정재고수량': item.optimalStock,
            '실재고수량': item.actualStock,
            '사유': item.reason
        }));
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '선택한적정재고');
        XLSX.writeFile(workbook, '선택한적정재고.xlsx');
    };
    // 전체 항목 엑셀 다운로드
    const handleDownloadAll = () => {
        const excelData = items.map(item => ({
            '상품코드': item.productCode,
            '상품명': item.productName,
            '규격': item.specification,
            '장부수량': item.currentStock,
            '적정재고수량': item.optimalStock,
            '실재고수량': item.actualStock,
            '사유': item.reason
        }));
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '적정재고목록');
        XLSX.writeFile(workbook, '적정재고목록.xlsx');
    };
    // 적정재고 형성 함수 추가
    const handleOptimalStockGeneration = () => {
        if (window.confirm('적정재고를 자동으로 형성하시겠습니까? 이 작업은 상품별 판매 이력과 재고 상황을 분석하여 적정 재고 수량을 계산합니다.')) {
            // 실제로는 API 호출을 통해 적정재고를 계산할 것입니다.
            // 여기서는 시뮬레이션을 위해 setTimeout 사용
            // 로딩 표시 등을 추가할 수 있습니다.
            setTimeout(() => {
                // 샘플 데이터를 업데이트하는 예제
                const updatedItems = items.map(item => {
                    // 판매량 기반 계산 로직 (실제로는 복잡한 알고리즘 사용)
                    const suggestedOptimalStock = Math.floor(item.currentStock * 1.2);
                    return {
                        ...item,
                        optimalStock: suggestedOptimalStock,
                        reason: item.reason || '시스템 자동 계산'
                    };
                });
                setItems(updatedItems);
                setFilteredItems(updatedItems);
                alert('적정재고 형성이 완료되었습니다.');
            }, 1500);
        }
    };
    // 페이지네이션 계산
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    // 페이지 변경 핸들러
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    // 페이지당 항목 수 변경 핸들러
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };
    return (_jsxs("div", { className: "container-fluid", children: [_jsx("div", { className: "row", children: _jsx("div", { className: "col-12", children: _jsxs("div", { className: "page-title-box", children: [_jsx("div", { className: "page-title-right", children: _jsxs("ol", { className: "breadcrumb m-0", children: [_jsx("li", { className: "breadcrumb-item", children: _jsx("a", { href: "javascript: void(0);", children: "\uAE30\uC900\uC815\uBCF4" }) }), _jsx("li", { className: "breadcrumb-item active", children: "\uC801\uC815\uC7AC\uACE0\uAD00\uB9AC" })] }) }), _jsx("h4", { className: "page-title", children: "\uC801\uC815\uC7AC\uACE0\uAD00\uB9AC" })] }) }) }), _jsx("div", { className: "row", children: _jsx("div", { className: "col-12", children: _jsx("div", { className: "card", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "row mb-3", children: _jsx("div", { className: "col-12", children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsxs("select", { className: "form-select flex-shrink-0", style: { width: '120px', borderRadius: '0.25rem 0 0 0.25rem' }, value: searchColumn, onChange: handleSearchColumnChange, children: [_jsx("option", { value: "productCode", children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("option", { value: "productName", children: "\uC0C1\uD488\uBA85" }), _jsx("option", { value: "specification", children: "\uADDC\uACA9" })] }), _jsx("input", { type: "text", className: "form-control", style: { borderRadius: '0', borderLeft: '0' }, placeholder: "\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD558\uC138\uC694", value: searchTerm, onChange: handleSearchChange, onKeyPress: (e) => e.key === 'Enter' && handleSearch() }), _jsx("button", { className: "btn btn-primary flex-shrink-0", type: "button", style: { borderRadius: '0 0.25rem 0.25rem 0' }, onClick: handleSearch, children: "\uAC80\uC0C9" }), _jsx("button", { className: "btn btn-secondary flex-shrink-0 ms-2", type: "button", onClick: clearFilters, children: "\uCD08\uAE30\uD654" }), _jsxs("div", { className: "ms-auto", children: [_jsx("button", { className: "btn btn-warning me-2", onClick: handleOptimalStockGeneration, children: "\uC801\uC815\uC7AC\uACE0 \uD615\uC131" }), _jsx("button", { className: "btn btn-success me-2", onClick: () => navigate('/optimal-stock/add'), children: "\uC801\uC815\uC7AC\uACE0\uB4F1\uB85D" }), _jsx("button", { className: "btn btn-primary me-2", onClick: toggleUploadModal, children: "\uC5D1\uC140 \uB300\uB7C9\uB4F1\uB85D" }), _jsx("button", { className: "btn btn-info me-2", onClick: handleDownloadSelected, disabled: selectedItemIds.length === 0, children: "\uC120\uD0DD \uB2E4\uC6B4\uB85C\uB4DC" }), _jsx("button", { className: "btn btn-info", onClick: handleDownloadAll, children: "\uC804\uCCB4 \uB2E4\uC6B4\uB85C\uB4DC" })] })] }) }) }), _jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-bordered table-hover", children: [_jsx("thead", { className: "table-light", children: _jsxs("tr", { children: [_jsx("th", { className: "text-center", style: { width: '40px' }, children: _jsx("input", { type: "checkbox", className: "form-check-input", onChange: handleSelectAll, checked: filteredItems.length > 0 && filteredItems.every(item => item.selected) }) }), _jsx("th", { className: "text-center", style: { width: '80px' }, children: "\uBC88\uD638" }), _jsx("th", { className: "text-center", style: { width: '120px' }, children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("th", { className: "text-center", style: { width: '80px' }, children: "\uC774\uBBF8\uC9C0" }), _jsx("th", { className: "text-center", children: "\uC0C1\uD488\uBA85" }), _jsx("th", { className: "text-center", children: "\uADDC\uACA9" }), _jsx("th", { className: "text-center", style: { width: '100px' }, children: "\uC7A5\uBD80\uC218\uB7C9" }), _jsx("th", { className: "text-center", style: { width: '100px' }, children: "\uC801\uC815\uC7AC\uACE0" }), _jsx("th", { className: "text-center", style: { width: '100px' }, children: "\uC2E4\uC7AC\uACE0" }), _jsx("th", { className: "text-center", children: "\uC0AC\uC720" }), _jsx("th", { className: "text-center", style: { width: '140px' }, children: "\uAD00\uB9AC" })] }) }), _jsx("tbody", { children: filteredItems.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 11, className: "text-center py-4", children: "\uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4." }) })) : (filteredItems.slice(indexOfFirstItem, indexOfLastItem).map((item, index) => (_jsxs("tr", { children: [_jsx("td", { className: "text-center align-middle", children: _jsx("input", { type: "checkbox", className: "form-check-input", checked: item.selected || false, onChange: () => handleSelectItem(item.id) }) }), _jsx("td", { className: "text-center align-middle", children: indexOfFirstItem + index + 1 }), _jsx("td", { className: "text-center align-middle", children: item.productCode }), _jsx("td", { className: "text-center align-middle", children: _jsx("img", { src: item.image, alt: item.productName, className: "img-thumbnail", style: { width: '50px', height: '50px', objectFit: 'cover' } }) }), _jsx("td", { className: "align-middle", children: item.productName }), _jsx("td", { className: "align-middle", children: item.specification }), _jsx("td", { className: "text-end align-middle", children: item.currentStock.toLocaleString() }), _jsx("td", { className: "text-end align-middle", children: item.optimalStock.toLocaleString() }), _jsx("td", { className: "text-end align-middle", children: item.actualStock.toLocaleString() }), _jsx("td", { className: "align-middle", children: item.reason }), _jsxs("td", { className: "text-center align-middle", children: [_jsx("button", { className: "btn btn-sm btn-outline-primary me-1", onClick: () => handleEditItem(item.id), children: "\uC218\uC815" }), _jsx("button", { className: "btn btn-sm btn-outline-danger", onClick: () => handleDeleteItem(item.id), children: "\uC0AD\uC81C" })] })] }, item.id)))) })] }) }), _jsxs("div", { className: "row", children: [_jsx("div", { className: "col-md-6", children: _jsxs("div", { className: "mt-2", children: ["\uCD1D ", _jsx("span", { className: "fw-bold", children: filteredItems.length }), "\uAC1C \uD56D\uBAA9 \uC911", ' ', _jsx("span", { className: "fw-bold", children: indexOfFirstItem + 1 }), " ~", ' ', _jsx("span", { className: "fw-bold", children: Math.min(indexOfLastItem, filteredItems.length) }), "\uAC1C \uD45C\uC2DC"] }) }), _jsx("div", { className: "col-md-6", children: _jsx("nav", { className: "float-end", children: _jsxs("ul", { className: "pagination pagination-rounded mb-0", children: [_jsx("li", { className: `page-item ${currentPage === 1 ? 'disabled' : ''}`, children: _jsx("button", { className: "page-link", onClick: () => handlePageChange(currentPage - 1), children: _jsx("i", { className: "mdi mdi-chevron-left" }) }) }), Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                            const pageNum = currentPage > 3 && totalPages > 5
                                                                ? currentPage - 2 + i
                                                                : i + 1;
                                                            return pageNum <= totalPages ? (_jsx("li", { className: `page-item ${pageNum === currentPage ? 'active' : ''}`, children: _jsx("button", { className: "page-link", onClick: () => handlePageChange(pageNum), children: pageNum }) }, pageNum)) : null;
                                                        }), _jsx("li", { className: `page-item ${currentPage === totalPages ? 'disabled' : ''}`, children: _jsx("button", { className: "page-link", onClick: () => handlePageChange(currentPage + 1), children: _jsx("i", { className: "mdi mdi-chevron-right" }) }) })] }) }) })] })] }) }) }) }), isUploadModalOpen && (_jsx("div", { className: "modal d-block", tabIndex: -1, role: "dialog", style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }, children: _jsx("div", { className: "modal-dialog modal-lg", role: "document", children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: "\uC801\uC815\uC7AC\uACE0 \uB300\uB7C9\uB4F1\uB85D" }), _jsx("button", { type: "button", className: "btn-close", onClick: toggleUploadModal, "aria-label": "Close" })] }), _jsxs("div", { className: "modal-body", children: [_jsxs("div", { className: "mb-3", children: [_jsxs("div", { className: "d-flex align-items-center mb-3", children: [_jsx("input", { type: "file", className: "form-control me-2", accept: ".xlsx, .xls", onChange: handleFileUpload }, fileInputKey), _jsx("button", { className: "btn btn-outline-primary", onClick: handleDownloadTemplate, children: "\uD15C\uD50C\uB9BF \uB2E4\uC6B4\uB85C\uB4DC" })] }), _jsxs("div", { className: "small text-muted", children: ["* \uC5D1\uC140 \uD30C\uC77C(.xlsx, .xls)\uB9CC \uC5C5\uB85C\uB4DC \uAC00\uB2A5\uD569\uB2C8\uB2E4.", _jsx("br", {}), "* \uD15C\uD50C\uB9BF \uB2E4\uC6B4\uB85C\uB4DC \uBC84\uD2BC\uC744 \uD074\uB9AD\uD558\uC5EC \uC591\uC2DD\uC744 \uB2E4\uC6B4\uB85C\uB4DC \uBC1B\uC744 \uC218 \uC788\uC2B5\uB2C8\uB2E4."] })] }), uploadedItems.length > 0 && (_jsx("div", { className: "table-responsive mt-3", children: _jsxs("table", { className: "table table-bordered", children: [_jsx("thead", { className: "table-light", children: _jsxs("tr", { children: [_jsx("th", { className: "text-center", children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("th", { className: "text-center", children: "\uC0C1\uD488\uBA85" }), _jsx("th", { className: "text-center", children: "\uADDC\uACA9" }), _jsx("th", { className: "text-center", children: "\uC7A5\uBD80\uC218\uB7C9" }), _jsx("th", { className: "text-center", children: "\uC801\uC815\uC7AC\uACE0" }), _jsx("th", { className: "text-center", children: "\uC2E4\uC7AC\uACE0" }), _jsx("th", { className: "text-center", children: "\uC0AC\uC720" })] }) }), _jsx("tbody", { children: uploadedItems.map((item, index) => (_jsxs("tr", { children: [_jsx("td", { children: item.productCode }), _jsx("td", { children: item.productName }), _jsx("td", { children: item.specification }), _jsx("td", { className: "text-end", children: item.currentStock.toLocaleString() }), _jsx("td", { className: "text-end", children: item.optimalStock.toLocaleString() }), _jsx("td", { className: "text-end", children: item.actualStock.toLocaleString() }), _jsx("td", { children: item.reason })] }, index))) })] }) }))] }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-secondary", onClick: toggleUploadModal, children: "\uCDE8\uC18C" }), _jsx("button", { type: "button", className: "btn btn-primary", onClick: handleRegisterUploadedItems, disabled: uploadedItems.length === 0, children: "\uB4F1\uB85D" })] })] }) }) }))] }));
};
export default OptimalStockManagement;
